
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase/client';
import AuthContext from '../context/AuthContext';
import { AuthUser } from '../types/AuthTypes';
import { checkRoleAccess } from '../utils/roleUtils';
import { roleService } from '@/services/auth/role/roleService';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = !!user;

  // Initialize auth state on component mount with error handling
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log("[AuthContext] Initializing auth...");
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("[AuthContext] Error getting auth session:", error);
          setUser(null);
          return;
        } 
        
        if (data.session) {
          console.log("[AuthContext] Session found, getting user data");
          const { data: userData, error: userError } = await supabase.auth.getUser();
          
          if (userError || !userData.user) {
            console.error("[AuthContext] Error getting user data:", userError);
            setUser(null);
            return;
          }
          
          console.log("[AuthContext] User data retrieved successfully");
          
          // Ensure user has a role
          if (userData.user.id) {
            await roleService.createUserRole(userData.user.id);
            const userRole = await roleService.fetchUserRole(userData.user.id);
            console.log("[AuthContext] User role:", userRole);
            
            const authUser: AuthUser = {
              id: userData.user.id,
              email: userData.user.email || '',
              role: userRole || 'USER',
              name: userData.user.user_metadata?.name,
              dealer_activation_status: userData.user.user_metadata?.dealer_activation_status || 'NONE',
              settings: {
                twoFactorEnabled: userData.user.user_metadata?.two_factor_enabled || false,
                theme: userData.user.user_metadata?.theme || 'light',
                notifications: userData.user.user_metadata?.notifications || false
              }
            };
            setUser(authUser);
          } else {
            setUser(null);
          }
        } else {
          console.log("[AuthContext] No active session found");
          setUser(null);
        }
      } catch (error) {
        console.error("[AuthContext] Auth initialization error:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
        console.log("[AuthContext] Auth initialization completed");
      }
    };

    initializeAuth();
  }, []);

  // Sign in function with improved error handling
  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    console.log("[AuthContext] Attempting sign in for:", email);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error("[AuthContext] Sign in error:", error);
        toast.error(error.message || 'Login fallito');
        return;
      }

      if (data.user) {
        console.log("[AuthContext] Sign in successful for:", data.user.id);
        
        // Ensure user has a role after login
        await roleService.createUserRole(data.user.id);
        const userRole = await roleService.fetchUserRole(data.user.id);
        console.log("[AuthContext] User role after login:", userRole);
        
        const authUser: AuthUser = {
          id: data.user.id,
          email: data.user.email || '',
          role: userRole || 'USER',
          name: data.user.user_metadata?.name,
          dealer_activation_status: data.user.user_metadata?.dealer_activation_status || 'NONE',
          settings: {
            twoFactorEnabled: data.user.user_metadata?.two_factor_enabled || false,
            theme: data.user.user_metadata?.theme || 'light',
            notifications: data.user.user_metadata?.notifications || false
          }
        };
        
        setUser(authUser);
        toast.success('Login effettuato con successo');
      }
    } catch (error: any) {
      console.error('[AuthContext] Login error:', error);
      toast.error(error.message || 'Login fallito');
    } finally {
      setIsLoading(false);
    }
  };

  // Sign up function with improved error handling and metadata support
  const signUp = async (email: string, password: string, name?: string) => {
    setIsLoading(true);
    console.log("[AuthContext] Attempting sign up for:", email, "with name:", name);
    
    try {
      // Include user metadata in the signup request
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name || '',
            role: 'USER',
            created_at: new Date().toISOString()
          },
          emailRedirectTo: 'https://www.gildy.it/auth/verify'
        }
      });

      if (error) {
        console.error("[AuthContext] Sign up error:", error);
        toast.error(error.message || 'Registrazione fallita');
        return;
      }

      if (data.user) {
        console.log("[AuthContext] Sign up successful for:", data.user.id);
        
        // Create default role for new user with retry mechanism
        try {
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              await roleService.createUserRole(data.user.id);
              const userRole = await roleService.fetchUserRole(data.user.id);
              console.log("[AuthContext] New user role created:", userRole);
              break;
            } catch (roleError) {
              console.error(`[AuthContext] Role creation attempt ${attempt + 1} failed:`, roleError);
              if (attempt === 2) throw roleError;
              // Wait a bit before retrying
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        } catch (roleError) {
          console.error("[AuthContext] All role creation attempts failed:", roleError);
          // Continue anyway since the user was created
        }
        
        const authUser: AuthUser = {
          id: data.user.id,
          email: data.user.email || '',
          role: 'USER', // Default role until confirmed
          name: name || data.user.user_metadata?.name,
          dealer_activation_status: 'NONE',
          settings: {
            twoFactorEnabled: false,
            theme: 'light',
            notifications: false
          }
        };
        
        setUser(authUser);
        toast.success('Registrazione completata con successo. Controlla la tua email per confermare l\'account.');
      }
    } catch (error: any) {
      console.error('[AuthContext] Registration error:', error);
      toast.error(error.message || 'Registrazione fallita');
    } finally {
      setIsLoading(false);
    }
  };

  // Sign out function with improved error handling
  const signOut = async () => {
    setIsLoading(true);
    console.log("[AuthContext] Attempting sign out");
    
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("[AuthContext] Sign out error:", error);
        toast.error(error.message || 'Logout fallito');
        return;
      }
      
      console.log("[AuthContext] Sign out successful");
      setUser(null);
      toast.success('Logout effettuato con successo');
    } catch (error: any) {
      console.error('[AuthContext] Logout error:', error);
      toast.error(error.message || 'Logout fallito');
      
      // Force user state to null even if the API call fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Role check function
  const hasRole = (role: string) => {
    if (!user) return false;
    return checkRoleAccess(user.role, role);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated,
      signIn,
      signUp,
      signOut,
      hasRole
    }}>
      {children}
    </AuthContext.Provider>
  );
}
